import { useState, useEffect } from "react";
import axios from "axios";
import { formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";
import { formatMillisecondsToTime } from "../../utils/formatTime";

const Leaderboards = () => {
  const [leaderboards, setLeaderboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchLeaderboards = async () => {
      try {
        const { data } = await axios.get("https://api.pixelore.wiki/leaderboards");
        setLeaderboards(data);
      } catch (err) {
        setError("Failed to load leaderboards");
        console.error("Error fetching leaderboards:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboards();
  }, []);

  const filteredLeaderboards = leaderboards.map((board) => ({
    ...board,
    players: searchTerm ? board.players.filter((p) => p.username.toLowerCase().includes(searchTerm.toLowerCase())) : board.players.slice(0, 30),
  }));

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is already handled by the filteredLeaderboards
  };

  if (loading) return <div className="p-8 text-center">Loading leaderboards...</div>;
  if (error) return <div className="p-8 text-center text-red-500">{error}</div>;
  if (!leaderboards.length) return <div className="p-8 text-center">No leaderboard data available</div>;

  return (
    <div className="panel-style">
      <h2 className="panel-heading">Leaderboards</h2>

      <form onSubmit={handleSearch} className="filter-bar mb-4 mx-auto w-full lg:w-96">
        <input
          type="text"
          placeholder="Search username..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
          className="flex-grow px-4 py-2 select-forms w-full"
        />
      </form>

      <div className="leaderboards-container w-full overflow-hidden">
        <div className="leaderboards-scroll flex gap-4 pb-4 overflow-x-auto">
          {filteredLeaderboards.map((board) => (
            <LeaderboardCard key={board.sourceId} board={board} searchTerm={searchTerm} />
          ))}
        </div>
      </div>
    </div>
  );
};

const LeaderboardCard = ({ board, searchTerm }) => {
  return (
    <div className="flex-shrink-0 w-72 rounded-lg shadow-sm overflow-hidden border  bg-[var(--entity-bg-color)] border-[var(--outline2-color)] text-[var(--text-color)]">
      {/* Header */}
      <div className="p-4 border-b border-[var(--outline2-color)]">
        <div className="flex justify-between items-baseline">
          <h3 className="text-lg font-bold text-wrap">{board.title}</h3>
          <span className="text-xs text-[var(--text5-color)]">{formatDistanceToNow(new Date(board.lastUpdated), { addSuffix: true })}</span>
        </div>
        <div className="text-xs text-[var(--text5-color)] mt-1">Source: {board.sourceId.startsWith("veronica") ? "VeronicaAI" : "Pixels.xyz"}</div>
      </div>

      {/* Players List */}
      <div className="divide-y divide-[var(--outline2-color)] max-h-96 overflow-y-auto font-leaderboard">
        {board.players.length > 0 ? (
          board.players.map((player) => (
            <div
              key={`${board.sourceId}-${player.rank}`}
              className={`p-3 hover:bg-[var(--hover-color)] ${
                searchTerm && player.username.toLowerCase().includes(searchTerm.toLowerCase()) ? "bg-[var(--entity-mini)]" : ""
              }`}
            >
              <div className="flex justify-between items-center text-sm">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-medium w-6 text-right flex-shrink-0">{player.rank}</span>
                  <Link to={`/lookup/players/${player.playerId}`} target="_blank" rel="noreferrer">
                    <span className="truncate">
                      {player.username}
                      <br />
                      {player.guildHandle && <span className="text-xs text-[var(--text5-color)] ml-1">[{player.guildHandle}]</span>}
                    </span>
                  </Link>
                </div>
                <span className="font-leaderboard flex-shrink-0">
                  {board.sourceId.startsWith("veronica") ? formatMillisecondsToTime(player.score) : player.score.toLocaleString()}
                </span>
              </div>
            </div>
          ))
        ) : (
          <div className="text-xs p-4 text-center text-[var(--text3-color)]">{searchTerm ? "No matching players" : "No data available"}</div>
        )}
      </div>
    </div>
  );
};

export default Leaderboards;
